import { Box } from 'lucide-vue-next'
import { type App, defineAsyncComponent } from 'vue'
import type { RouteRecordName } from 'vue-router'
import { registerComponents } from './components'

export { useBrandStore } from './store'

export type BrandFlowDestination = {
  name: RouteRecordName
  params?: Record<string, string>
}

export type BrandFlow = {
  next?: BrandFlowDestination
  exit: BrandFlowDestination
}

declare module 'vue-router' {
  interface RouteMeta {
    brandFlow?: BrandFlow
  }
}

const WORKSPACE = { labelKey: 'nav.workspace', to: { name: 'dashboard' } }
const BRANDS = { labelKey: 'brands.title', to: { name: 'brands' } }

export const createBrands = () => ({
  install: (app: App) => {
    registerComponents(app)
    const router = app.getRouter()

    router.addRoute('organization', {
      path: 'brands',
      name: 'brands',
      meta: {
        head: { titleKey: 'brands.title' },
        nav: {
          icon: Box,
          order: 10,
          section: 'nav.workspace',
          trailing: defineAsyncComponent(() => import('./components/BrandNavCount.vue'))
        },
        crumbs: [WORKSPACE, { labelKey: 'brands.title' }]
      },
      component: () => import('./pages/BrandsPage.vue')
    })

    router.addRoute('organization', {
      path: 'brands/new',
      name: 'brand-new',
      meta: {
        head: { titleKey: 'brands.newTitle' },
        crumbs: [WORKSPACE, BRANDS, { labelKey: 'brands.newTitle' }],
        brandFlow: {
          next: { name: 'brand-review' },
          exit: { name: 'brand' }
        }
      },
      props: { create: true },
      component: () => import('./pages/BrandPage.vue')
    })

    router.addRoute('organization', {
      path: 'brands/:brandId/review',
      name: 'brand-review',
      meta: {
        head: { titleKey: 'brands.review.pageTitle' },
        crumbs: [WORKSPACE, BRANDS, { labelKey: 'brands.review.crumb' }],
        brandFlow: {
          next: { name: 'brand-typography' },
          exit: { name: 'brand' }
        }
      },
      component: () => import('./pages/BrandReviewPage.vue')
    })

    router.addRoute('organization', {
      path: 'brands/:brandId/typography',
      name: 'brand-typography',
      meta: {
        head: { titleKey: 'brands.typography.pageTitle' },
        crumbs: [WORKSPACE, BRANDS, { labelKey: 'brands.typography.crumb' }],
        brandFlow: { exit: { name: 'brand' } }
      },
      component: () => import('./pages/BrandTypographyPage.vue')
    })

    router.addRoute('organization', {
      path: 'brands/:brandId',
      name: 'brand',
      meta: {
        head: { titleKey: 'brands.title' },
        crumbs: [WORKSPACE, BRANDS, { labelKey: 'brands.detailCrumb' }],
        brandFlow: {
          next: { name: 'brand-review' },
          exit: { name: 'brand' }
        }
      },
      props: { create: false },
      component: () => import('./pages/BrandPage.vue')
    })
  }
})
